/**
 * Hand-off from a bulk add on Lead Search to the Leads page (owner request,
 * #dca-dev 2026-10-23: "a step after bulk import that takes them to the leads
 * that were just imported and highlights each with the enrich button").
 *
 * Bulk adds skip research, so the Lead Search page remembers the new lead ids
 * here and links to /leads?imported=1, which selects exactly those leads with
 * the Research button ready. Session storage keeps long id lists out of the
 * URL; entries older than 30 minutes are ignored.
 */

const KEY = 'dca:just-imported'
const TTL_MS = 30 * 60_000

export const JUST_IMPORTED_PATH = '/leads?imported=1'

export function rememberImported(ids: readonly string[], now = Date.now()): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ ids, at: now }))
  } catch {
    /* storage unavailable: the Leads page simply opens without a selection */
  }
}

/** Reads and clears the remembered ids. Empty when missing, expired or unreadable. */
export function takeImported(now = Date.now()): string[] {
  try {
    const raw = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as { ids?: unknown; at?: unknown }
    if (typeof parsed.at !== 'number' || now - parsed.at > TTL_MS || !Array.isArray(parsed.ids)) return []
    return parsed.ids.filter((id): id is string => typeof id === 'string' && id.length > 0)
  } catch {
    return []
  }
}

/**
 * After a Research click, the imported ids that did not run. The enrich burst
 * limit stops a bulk call after a few leads (about 4 a minute, 6 on Custom), so
 * "Research 40" usually researches a handful; these stay in the banner as
 * "Research the remaining N" instead of the list being lost.
 */
export function remainingImported(
  imported: ReadonlySet<string>,
  results: ReadonlyArray<{ readonly lead_id: string; readonly status: string }>,
): Set<string> {
  const notRun = new Set(results.filter((r) => r.status === 'rate_limited').map((r) => r.lead_id))
  return new Set([...imported].filter((id) => notRun.has(id)))
}
